/**
 * CandidateCarousel - infinite auto-scrolling candidate marquee
 * Uses CSS animation for smooth infinite scroll
 */
import { useRef, useState } from 'react';
import styles from './CandidateCarousel.module.css';
import { CandidateCard } from '@/components/features/CandidateCard';
import { useCandidates } from '@/api';
import type { CandidateListItem } from '@/types/elections';

interface CandidateCarouselProps {
    onCandidateSelect?: (candidate: CandidateListItem) => void;
    selectedCandidateId?: number | null;
}

// Skeleton card for loading state
function SkeletonCard() {
    return (
        <div className={styles.skeletonCard}>
            <div className={styles.skeletonPhoto} />
            <div className={styles.skeletonName} />
            <div className={styles.skeletonParty} />
        </div>
    );
}

export function CandidateCarousel({ onCandidateSelect, selectedCandidateId }: CandidateCarouselProps) {
    const trackRef = useRef<HTMLDivElement>(null);
    const [isPaused, setIsPaused] = useState(false);
    const { data: candidates, isLoading, error } = useCandidates();

    // Pause auto-scroll when user interacts
    const handleMouseEnter = () => setIsPaused(true);
    const handleMouseLeave = () => setIsPaused(false);

    // Show skeleton loading state
    if (isLoading) {
        return (
            <div className={styles.container}>
                <div className={styles.track}>
                    {Array.from({ length: 8 }).map((_, i) => (
                        <SkeletonCard key={i} />
                    ))}
                </div>
            </div>
        );
    }

    // Show error state
    if (error) {
        return (
            <div className={styles.container}>
                <div className={styles.errorState}>
                    <span>Error al cargar candidatos</span>
                    <button onClick={() => window.location.reload()}>Reintentar</button>
                </div>
            </div>
        );
    }

    // Filter for President candidates only (as per user request)
    const presidentialCandidates = candidates?.filter(c =>
        c.position.toLowerCase() === 'presidente'
    ) || [];

    // No candidates found after filtering
    if (presidentialCandidates.length === 0) {
        return (
            <div className={styles.container}>
                <div className={styles.emptyState}>
                    No hay candidatos presidenciales disponibles
                </div>
            </div>
        );
    }

    // Duplicate candidates for seamless infinite scroll
    const duplicatedCandidates = [...presidentialCandidates, ...presidentialCandidates];

    return (
        <div
            className={styles.container}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div className={styles.mask}>
                <div
                    ref={trackRef}
                    className={`${styles.track} ${isPaused ? styles.paused : ''}`}
                    style={{
                        '--candidate-count': presidentialCandidates.length,
                    } as React.CSSProperties}
                >
                    {duplicatedCandidates.map((candidate, index) => (
                        <CandidateCard
                            key={`${candidate.id}-${index}`}
                            candidate={candidate}
                            onClick={onCandidateSelect}
                            isSelected={selectedCandidateId === candidate.id}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}
